import { useQuery, type UseQueryOptions, type UseQueryResult } from '@tanstack/react-query';
import {
  getCurrentBooster,
  type BECurrentBooster,
  type CurrentBoosterResponse,
  type GetCurrentBoosterParams,
} from '@/lib/network';

export const CURRENT_BOOSTER_QUERY_KEY_PREFIX = ['boosters', 'current'] as const;

export const getCurrentBoosterQueryKey = (
  tournamentCalendarId: string | undefined,
  matchdayId: string | undefined,
  gameweekId: string | undefined
) =>
  [
    ...CURRENT_BOOSTER_QUERY_KEY_PREFIX,
    tournamentCalendarId ?? '',
    matchdayId ?? '',
    gameweekId ?? '',
  ] as const;

export type UseCurrentBoosterOptions = Omit<
  UseQueryOptions<CurrentBoosterResponse, Error, BECurrentBooster | null>,
  'queryKey' | 'queryFn' | 'select'
>;

export const useCurrentBooster = (
  params: Partial<GetCurrentBoosterParams>,
  options?: UseCurrentBoosterOptions
): UseQueryResult<BECurrentBooster | null, Error> => {
  const { enabled = true, ...restOptions } = options ?? {};
  const tournamentCalendarId = params.tournament_calendar_id;
  const matchdayId = params.matchday_id;
  const gameweekId = params.gameweek_id;

  return useQuery<CurrentBoosterResponse, Error, BECurrentBooster | null>({
    queryKey: getCurrentBoosterQueryKey(tournamentCalendarId, matchdayId, gameweekId),
    queryFn: async () => {
      if (!tournamentCalendarId) {
        throw new Error('tournament_calendar_id is required');
      }
      if (!matchdayId && !gameweekId) {
        throw new Error('Either matchday_id or gameweek_id is required');
      }
      return getCurrentBooster({
        tournament_calendar_id: tournamentCalendarId,
        matchday_id: matchdayId,
        gameweek_id: gameweekId,
      });
    },
    select: response => response.data ?? null,
    enabled: enabled && !!tournamentCalendarId && (!!matchdayId || !!gameweekId),
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    ...restOptions,
  });
};
